/**
 * Sector Operations — Clinics Specialty Loops (Outside Job Orders, Dialyzer Reuse, IVF Witnessing).
 * R39 M17.a (plan docs/plans/sector-operations.md §2 SO-P268, SO-D227).
 */

// ---------------------------------------------------------------------------
// 1. Outside Job Orders (Dental crown/aligner, Optical lens)
// ---------------------------------------------------------------------------

export const CLI_JOB_ORDER_KINDS = [
  'DENTAL_CROWN',
  'DENTAL_ALIGNER',
  'OPTICAL_LENS',
] as const
export type CliJobOrderKind = (typeof CLI_JOB_ORDER_KINDS)[number]

export const CLI_JOB_ORDER_STAGES = [
  'SENT',
  'AT_VENDOR',
  'RETURNED',
  'FITTED',
  'REMAKE',
] as const
export type CliJobOrderStage = (typeof CLI_JOB_ORDER_STAGES)[number]

export interface OpsCliJobOrderStageEvent {
  stage: CliJobOrderStage
  at: number
  by: string
  photoPath?: string
  remakeReason?: string
  note?: string
}

export interface OpsCliJobOrder {
  id: string
  workspaceId: string
  kind: CliJobOrderKind
  caseToken: string // barcode or generated short token; never a patient name
  patientToken?: string
  placeId: string
  vendorName: string
  vendorPartyId?: string
  stage: CliJobOrderStage
  remakeCount: number
  dueAt: number
  expectedReturnAt?: number
  sentPhotoPath?: string
  returnedPhotoPath?: string
  overdueRequestRaised?: boolean
  stages: OpsCliJobOrderStageEvent[]
  createdAt: number
  createdBy: string
  updatedAt: number
  updatedBy: string
  opId?: string
}

export function isValidJobOrderStageTransition(
  current: CliJobOrderStage,
  next: CliJobOrderStage,
): boolean {
  switch (current) {
    case 'SENT':
      return next === 'AT_VENDOR'
    case 'AT_VENDOR':
      return next === 'RETURNED'
    case 'RETURNED':
      return next === 'FITTED' || next === 'REMAKE'
    case 'REMAKE':
      return next === 'SENT'
    case 'FITTED':
      return false
    default:
      return false
  }
}

const JOB_ORDER_PHOTO_REGEX =
  /^workspaces\/([^/]+)\/ops_cli_job_orders\/([^/]+)\/([^/]+)\/[^/]+$/

export function isValidJobOrderPhotoPath(
  workspaceId: string,
  orderId: string,
  uid: string,
  photoPath: string,
): boolean {
  if (typeof photoPath !== 'string' || !photoPath.trim()) return false
  const match = photoPath.trim().match(JOB_ORDER_PHOTO_REGEX)
  if (!match) return false
  return match[1] === workspaceId && match[2] === orderId && match[3] === uid
}

// ---------------------------------------------------------------------------
// 2. Dialyzer Reuse (Max reuses 1–20, rinse/germicide/residual tests)
// ---------------------------------------------------------------------------

export const CLI_DIALYZER_STATES = ['ACTIVE', 'DISCARDED'] as const
export type CliDialyzerState = (typeof CLI_DIALYZER_STATES)[number]

export const CLI_DIALYZER_DISCARD_REASONS = [
  'MAX_REUSES_REACHED',
  'TEST_FAILED',
  'MANUAL_DISCARD',
] as const
export type CliDialyzerDiscardReason = (typeof CLI_DIALYZER_DISCARD_REASONS)[number]

export interface OpsCliDialyzer {
  id: string
  workspaceId: string
  dialyzerToken: string
  patientToken: string // booking token only
  placeId?: string
  maxReuses: number // workspace setting, default 6, range 1-20
  uses: number
  useCount?: number
  state: CliDialyzerState
  status?: CliDialyzerState | string
  discardReason?: CliDialyzerDiscardReason | string
  discardedAt?: number
  discardedBy?: string
  lastUseAt?: number
  lastUsedAt?: number
  rinsePassed?: boolean
  germicidePresent?: boolean
  residualTested?: boolean
  createdAt: number
  createdBy: string
  updatedAt: number
  updatedBy: string
  opId?: string
}

export function validateDialyzerUsePreconditions(
  dialyzer: OpsCliDialyzer,
  patientToken: string,
  maxReusesSetting?: number,
): { valid: boolean; reason?: string } {
  if (dialyzer.state === 'DISCARDED') {
    return { valid: false, reason: 'Dialyzer is discarded' }
  }
  if (dialyzer.patientToken.trim() !== patientToken.trim()) {
    return {
      valid: false,
      reason: 'Dialyzer registered to a different patient token',
    }
  }
  const maxLimit = maxReusesSetting ?? dialyzer.maxReuses ?? 6
  if (dialyzer.uses >= maxLimit) {
    return { valid: false, reason: 'Maximum dialyzer reuses reached' }
  }
  return { valid: true }
}

// ---------------------------------------------------------------------------
// 3. IVF Witnessing (Dual-person check, blind second-person confirm)
// ---------------------------------------------------------------------------

export const CLI_IVF_WITNESS_STEPS = [
  'COLLECTION',
  'INSEMINATION',
  'TRANSFER',
  'FREEZE',
  'THAW',
] as const
export type CliIvfWitnessStepKind = (typeof CLI_IVF_WITNESS_STEPS)[number]

export const CLI_IVF_WITNESS_STATUSES = [
  'PENDING_WITNESS',
  'CONFIRMED',
  'MISMATCH',
] as const
export type CliIvfWitnessStatus = (typeof CLI_IVF_WITNESS_STATUSES)[number]

export interface OpsCliIvfWitnessStep {
  id: string
  workspaceId: string
  caseToken: string
  stepKind: CliIvfWitnessStepKind
  step?: CliIvfWitnessStepKind | string
  operatorUid: string
  witnessUid: string
  status: CliIvfWitnessStatus
  witnessCaseToken?: string
  confirmedAt?: number
  mismatchRequestId?: string
  createdAt: number
  createdBy?: string
  updatedAt: number
  updatedBy?: string
  opId?: string
}

// ---------------------------------------------------------------------------
// 4. Room Turnaround Gate Helper
// ---------------------------------------------------------------------------

export function isTurnaroundRequiredForBooking(
  booking?: { resources?: { turnaroundRequired?: boolean } },
  bookable?: { resources?: { turnaroundRequired?: boolean } },
): boolean {
  return Boolean(
    booking?.resources?.turnaroundRequired ||
      bookable?.resources?.turnaroundRequired,
  )
}

// ---------------------------------------------------------------------------
// 5. Types & Helper Aliases for Callables & Client
// ---------------------------------------------------------------------------

export const DEFAULT_MAX_DIALYZER_REUSES = 6
export const MIN_MAX_DIALYZER_REUSES = 1
export const MAX_MAX_DIALYZER_REUSES = 20

export const OPS_CLI_JOB_ORDER_KINDS = CLI_JOB_ORDER_KINDS
export const OPS_CLI_JOB_ORDER_STAGES = CLI_JOB_ORDER_STAGES
export const OPS_CLI_DIALYZER_STATUSES = CLI_DIALYZER_STATES
export const OPS_CLI_IVF_WITNESS_STEPS = CLI_IVF_WITNESS_STEPS
export const OPS_CLI_IVF_WITNESS_STATUSES = CLI_IVF_WITNESS_STATUSES

export type OpsCliJobOrderKind = CliJobOrderKind
export type OpsCliJobOrderStage = CliJobOrderStage
export type OpsCliDialyzerStatus = CliDialyzerState
export type OpsCliIvfWitness = OpsCliIvfWitnessStep
export type OpsCliIvfWitnessStepType = CliIvfWitnessStepKind
export type OpsCliIvfWitnessStatus = CliIvfWitnessStatus

export interface CreateJobOrderInput {
  workspaceId: string
  kind: CliJobOrderKind
  caseToken: string
  placeId: string
  vendorName: string
  dueAt: number
  sentPhotoPath?: string
  patientToken?: string
  vendorPartyId?: string
  opId?: string
}

export interface UpdateJobOrderStageInput {
  workspaceId: string
  orderId: string
  nextStage: CliJobOrderStage
  photoPath?: string
  remakeReason?: string
  note?: string
  opId?: string
}

export interface SweepOverdueJobOrdersInput {
  workspaceId: string
}

export interface RegisterDialyzerInput {
  workspaceId: string
  dialyzerToken: string
  patientToken: string
  placeId?: string
  maxReuses?: number
  opId?: string
}

export interface RecordDialyzerUseInput {
  workspaceId: string
  dialyzerId: string
  patientToken?: string
  rinsePassed: boolean
  germicidePresent: boolean
  residualTested: boolean
  opId?: string
}

export interface CreateIvfWitnessStepInput {
  workspaceId: string
  caseToken: string
  stepKind?: CliIvfWitnessStepKind
  step?: CliIvfWitnessStepKind
  witnessUid: string
  opId?: string
}

export interface ConfirmIvfWitnessStepInput {
  workspaceId: string
  stepId: string
  confirmedCaseToken: string
  opId?: string
}

export const isValidJobOrderKind = (val: unknown): val is CliJobOrderKind =>
  typeof val === 'string' && (CLI_JOB_ORDER_KINDS as readonly unknown[]).includes(val)

export const isValidJobOrderStage = (val: unknown): val is CliJobOrderStage =>
  typeof val === 'string' && (CLI_JOB_ORDER_STAGES as readonly unknown[]).includes(val)

export const isAllowedJobOrderTransition = isValidJobOrderStageTransition

export const isValidDialyzerStatus = (val: unknown): val is CliDialyzerState =>
  typeof val === 'string' && (CLI_DIALYZER_STATES as readonly unknown[]).includes(val)

export const isValidIvfWitnessStep = (val: unknown): val is CliIvfWitnessStepKind =>
  typeof val === 'string' && (CLI_IVF_WITNESS_STEPS as readonly unknown[]).includes(val)

export const isValidIvfWitnessStatus = (val: unknown): val is CliIvfWitnessStatus =>
  typeof val === 'string' && (CLI_IVF_WITNESS_STATUSES as readonly unknown[]).includes(val)

export const validateJobOrderPhotoPath = isValidJobOrderPhotoPath

